import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ConcertTicket from '@/components/ConcertTicket';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchOrderByHash, fetchOrderReceiptInfo } from '@/apiEndpoints';
import { generateOrderReceiptPDF } from '@/utils/receiptPdf';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import MobileBottomNav from '@/components/MobileBottomNav';
import { currencySymbol } from '@/utils/currency';
import { IconAlertTriangle as AlertTriangle, IconCheck as Check, IconCopy as Copy, IconInfoCircle as Info, IconLoader2 as Loader2, IconSearch as SearchIcon } from '@tabler/icons-react';
import { wrap, pageBg, displayTitle, eyebrow, bodyMuted, card, surface, cardTitle, btnPrimary, btnAccent, btnGhost, input, label, glow } from '@/components/ui/design';

// Type for order details based on actual API response
interface OrderDetails {
    success: boolean;
    data: {
        _id: string;
        orderId: string;
        hashedUniqueId: string;
        tickets: Array<{
            ticketId: {
                _id: string;
                eventId: string;
                name: string;
                quantity: number;
                price: string;
                type: string;
                description: string;
                status: boolean;
                saleStart: string;
                saleEnd: string;
                remained: number;
                createdAt: string;
                updatedAt: string;
                __v: number;
            };
            seatData: any;
            quantity: number;
            price: number;
            type: string;
            selectedDate: string;
            qrCode: string;
            event: {
                _id: string;
                title: string;
                description: string;
                category: string;
                times: Array<{
                    startDate: string;
                    endDate: string;
                }>;
                address: {
                    fullAddress: string;
                    country: string;
                    state: string;
                    city: string;
                    zipcode: string;
                };
                venue: string;
                lat: string;
                lng: string;
                eventType: string;
                price: string;
                userId: string;
                organizerId: string;
                likes: number;
                isNew: boolean;
                status: string;
                createdAt: string;
                updatedAt: string;
                __v: number;
                banner?: {
                    path: string;
                    link: string;
                };
            };
        }>;
        totalAmount: number;
        currency?: string;
        status: string;
        paymentId: string;
        qrCode: string;
        attendeeName: string;
        guestCartId: string;
        guestEmail: string;
        guestPhone: string;
        createdAt: string;
        updatedAt: string;
        __v: number;
    };
}

export default function OrderDetails() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const id = searchParams.get('id');
    const from = searchParams.get('from');
    const lastName = searchParams.get('lastName');
    const orderHash = typeof id === 'string' ? id : '';
    const orderLastName = typeof lastName === 'string' ? lastName : '';
    const [copySuccess, setCopySuccess] = useState(false);
    const [inputOrderId, setInputOrderId] = useState('');
    const [inputName, setInputName] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [isFromPayment, setIsFromPayment] = useState(false);
    const [isDirectAccess, setIsDirectAccess] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);

    // Initialize input with query parameter if available
    useEffect(() => {
        if (typeof id === 'string' && id) {
            setInputOrderId(id);
        }
        if (typeof lastName === 'string' && lastName) {
            setInputName(lastName);
        }
        // Check if user came from payment flow
        if (typeof from === 'string' && from === 'payment') {
            setIsFromPayment(true);
        }
        // Check if this is direct access (no 'from' parameter but has 'id')
        if (typeof id === 'string' && id && !from) {
            setIsDirectAccess(true);
        }
        // Reset states if from=search (don't show copy section)
        if (typeof from === 'string' && from === 'search') {
            setIsFromPayment(false);
            setIsDirectAccess(false);
        }
        // Reset search state when no order hash (back to search form)
        if (!orderHash) {
            setIsSearching(false);
            setSearchError('');
        }
        setIsInitialized(true);
    }, [id, from, lastName, orderHash]);

    // Fetch order details by hash and lastname
    const { data: orderResponse, isLoading, isError, error } = useQuery<OrderDetails>({
        queryKey: ['orderDetails', orderHash, orderLastName],
        queryFn: async () => {
            if (!orderHash) throw new Error('No order hash provided');
            return await fetchOrderByHash(orderHash, orderLastName);
        },
        enabled: !!orderHash,
        retry: 2,
        retryDelay: 1000,
    });

    const copyOrderNumber = async () => {
        if (typeof id === 'string' && id && orderResponse?.data) {
            try {
                await navigator.clipboard.writeText(orderResponse.data.hashedUniqueId);
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            } catch (err) {
                console.error('Failed to copy order number:', err);
            }
        }
    };

    const handleSearchOrder = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputOrderId.trim()) {
            setSearchError('Please enter an order number or hash ID');
            return;
        }
        if (!inputName.trim()) {
            setSearchError('Please enter your name');
            return;
        }

        setIsSearching(true);
        setSearchError('');

        try {
            // Navigate to the order page with both order ID and lastname as query parameters
            navigate(`/order?id=${encodeURIComponent(inputOrderId.trim())}&lastName=${encodeURIComponent(inputName.trim())}&from=search`);
        } catch (error) {
            setSearchError('Failed to search for order. Please try again.');
            setIsSearching(false);
        }
    };

    const handleDownloadReceipt = async () => {
        const order = orderResponse?.data;
        if (!order || isDownloadingReceipt) return;
        setIsDownloadingReceipt(true);
        const receiptTab = window.open('', '_blank');
        try {
            const res = await fetchOrderReceiptInfo(orderHash, orderLastName);
            const pdf = await generateOrderReceiptPDF(
                order,
                { name: getAttendeeName(order), email: getEmail(order), phone: getPhone(order) },
                res?.data || {}
            );
            const url = URL.createObjectURL(pdf);
            if (receiptTab) receiptTab.location.href = url;
            else window.location.href = url;
        } catch (err) {
            receiptTab?.close();
            console.error('Failed to generate receipt', err);
        } finally {
            setIsDownloadingReceipt(false);
        }
    };

    // Helper function to safely format date
    const formatDate = (dateString: string | undefined | null): string => {
        if (!dateString) return 'TBD';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'TBD';
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'TBD';
        }
    };

    // Helper function to safely format time
    const formatTime = (startDate: string | undefined | null, endDate: string | undefined | null): string => {
        if (!startDate || !endDate) return 'TBD';
        try {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'TBD';

            const startTime = start.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
            const endTime = end.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
            return `${startTime} to ${endTime}`;
        } catch (error) {
            console.error('Error formatting time:', error);
            return 'TBD';
        }
    };

    // Helper function to safely format address
    const formatAddress = (address: any): string => {
        if (!address) return '';

        if (address.fullAddress) {
            return address.fullAddress;
        }

        const city = address.city || '';
        const state = address.state || '';
        const combined = `${city}, ${state}`.replace(/^,\s*/, '').replace(/,\s*$/, '');
        return combined || '';
    };

    // Helper function to safely get attendee name for a specific ticket (prefer ticket attendeeName)
    const getAttendeeName = (orderData: any, ticket?: any): string => {
        return ticket?.attendeeName ||
            orderData?.attendeeName ||
            orderData?.guestName ||
            orderData?.customerName ||
            'Guest';
    };

    // Helper function to safely get email
    const getEmail = (orderData: any): string => {
        return orderData?.guestEmail ||
            orderData?.customerEmail ||
            'Not provided';
    };

    // Helper function to safely get phone
    const getPhone = (orderData: any): string => {
        return orderData?.guestPhone ||
            orderData?.customerPhone ||
            '';
    };

    // Show loading if we have an order hash (waiting for data)
    if (!isInitialized) {
        return (
            <div className={`min-h-screen ${pageBg}`}>
                <Header />
                <div className={`${wrap} flex min-h-[70vh] flex-col items-center justify-center py-16`}>
                    <div className="flex flex-col items-center text-center">
                        <Loader2 className="mb-5 h-8 w-8 animate-spin text-primary dark:text-nightAccent" />
                        <div className="mb-2 text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white">Loading your tickets...</div>
                        <div className="text-sm text-grey40 dark:text-nightMuted">Please wait while we retrieve your ticket information</div>
                    </div>
                </div>
                <Footer hasMobileNav />
                <MobileBottomNav />
            </div>
        );
    }

    // Show search form only if no order hash and initialized
    if (!orderHash) {
        return (
            <div className={`min-h-screen ${pageBg}`}>
                <Header />
                <section className="relative isolate overflow-hidden">
                <div className={glow} />
                <div className={`${wrap} flex min-h-[70vh] flex-col items-center py-12 sm:py-20`}>
                    <div className="w-full max-w-lg">
                        <div className="mb-10 text-center">
                            <p className={eyebrow}>Order lookup</p>
                            <h1 className={`${displayTitle} mt-4`}>Find Your Tickets</h1>
                            <p className={`${bodyMuted} mx-auto mt-6 max-w-md`}>Enter your order number/hash ID and name to retrieve your tickets</p>
                        </div>

                        <div className={`${card} p-6 sm:p-8`}>
                            <form onSubmit={handleSearchOrder} className="space-y-5">
                                <div>
                                    <label htmlFor="orderId" className={label}>
                                        Order Number / Hash ID
                                    </label>
                                    <input
                                        type="text"
                                        id="orderId"
                                        value={inputOrderId}
                                        onChange={(e) => setInputOrderId(e.target.value)}
                                        placeholder="Enter your order number or hash ID"
                                        className={input}
                                        disabled={isSearching}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="name" className={label}>
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={inputName}
                                        onChange={(e) => setInputName(e.target.value)}
                                        placeholder="Enter your name"
                                        className={input}
                                        disabled={isSearching}
                                    />
                                </div>

                                {searchError && (
                                    <div className="rounded-2xl bg-red-50 p-3 ring-1 ring-red-200 dark:bg-red-900/20 dark:ring-red-800">
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="text-sm text-red-700 dark:text-red-400">{searchError}</span>
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isSearching || !inputOrderId.trim() || !inputName.trim()}
                                    className={`${btnPrimary} w-full`}
                                >
                                    {isSearching ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Searching...
                                        </>
                                    ) : (
                                        <>
                                            <SearchIcon className="h-4 w-4" />
                                            Find My Tickets
                                        </>
                                    )}
                                </button>
                            </form>

                            <div className={`${surface} mt-6 rounded-2xl p-4`}>
                                <div className="flex items-start gap-3">
                                    <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary dark:text-nightAccent" />
                                    <div className="text-sm text-grey30 dark:text-nightTextSecondary">
                                        <strong>Need help?</strong> If you can't find your order number or hash ID, check your email confirmation or contact our support team.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </section>
                <Footer hasMobileNav />
                <MobileBottomNav />
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${pageBg}`}>
            <Header />
            <section className="relative isolate overflow-hidden">
            <div className={glow} />
            <div className={`${wrap} flex min-h-[70vh] flex-col items-center py-12 sm:py-16`}>
                {isLoading && (
                    <div className="flex flex-col items-center py-16 text-center">
                        <Loader2 className="mb-5 h-8 w-8 animate-spin text-primary dark:text-nightAccent" />
                        <div className="mb-2 text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white">Loading your tickets...</div>
                        <div className="text-sm text-grey40 dark:text-nightMuted">Please wait while we retrieve your ticket information</div>
                    </div>
                )}

                {isError && (() => {
                    // Try to extract orderStatus from backend error payload
                    // NestJS NotFoundException puts the message in error.response.data.message (axios)
                    let orderStatus: string | null = null;
                    const axiosError = error as any;
                    const candidates: string[] = [];
                    if (axiosError?.response?.data?.message) candidates.push(axiosError.response.data.message);
                    if (error instanceof Error) candidates.push(error.message);

                    for (const candidate of candidates) {
                        try {
                            const parsed = JSON.parse(candidate);
                            if (parsed?.orderStatus) { orderStatus = parsed.orderStatus; break; }
                        } catch {
                            // not JSON, ignore
                        }
                    }

                    const statusLabels: Record<string, { label: string; description: string; color: string }> = {
                        refunded: {
                            label: 'Order Refunded',
                            description: 'This order has been refunded. The tickets are no longer valid.',
                            color: 'orange',
                        },
                        pending_refund: {
                            label: 'Refund Pending',
                            description: 'A refund has been requested for this order and is currently being processed.',
                            color: 'yellow',
                        },
                        disputed: {
                            label: 'Order Disputed',
                            description: 'This order is currently under dispute. Please contact support for assistance.',
                            color: 'red',
                        },
                        cancelled: {
                            label: 'Order Cancelled',
                            description: 'This order has been cancelled and the tickets are no longer valid.',
                            color: 'red',
                        },
                        pending: {
                            label: 'Payment Pending',
                            description: 'Payment for this order is still pending. Tickets will be available once payment is confirmed.',
                            color: 'yellow',
                        },
                        failed: {
                            label: 'Payment Failed',
                            description: 'The payment for this order failed. Please try placing a new order.',
                            color: 'red',
                        },
                    };

                    if (orderStatus) {
                        const info = statusLabels[orderStatus.replace(/-/g, '_')] ?? {
                            label: `Order Status: ${orderStatus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`,
                            description: 'This order cannot be displayed because its current status does not allow ticket access.',
                            color: 'orange',
                        };
                        const colorMap: Record<string, string> = {
                            orange: 'bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-300',
                            yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300',
                            red: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300',
                        };
                        const iconColorMap: Record<string, string> = {
                            orange: 'text-orange-500',
                            yellow: 'text-yellow-500',
                            red: 'text-red-500',
                        };
                        const colorClass = colorMap[info.color] ?? colorMap.orange;
                        const iconClass = iconColorMap[info.color] ?? iconColorMap.orange;
                        return (
                            <div className="flex w-full max-w-xl flex-col items-center text-center">
                                <p className={eyebrow}>Your order</p>
                                <div className={`mt-4 inline-block px-4 py-2 rounded-full text-sm font-semibold mb-6 border ${colorClass}`}>
                                    {info.label}
                                </div>
                                <div className={`${bodyMuted} mb-8 max-w-md mx-auto`}>{info.description}</div>
                                <div className={`mb-8 w-full p-4 border rounded-2xl max-w-md mx-auto text-left ${colorClass}`}>
                                    <div className="flex items-start gap-2">
                                        <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                        <div className="text-sm">
                                            If you believe this is a mistake, please contact our support team with your order number.
                                        </div>
                                    </div>
                                </div>
                                <div className="flex w-full max-w-md flex-col gap-3 sm:flex-row">
                                    <Link to="/order" className={`${btnPrimary} w-full sm:flex-1`}>
                                        Try Another Search
                                    </Link>
                                    <Link to="/" className={`${btnGhost} w-full sm:flex-1`}>
                                        Return to Home
                                    </Link>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div className="flex w-full max-w-xl flex-col items-center text-center">
                            <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                                <AlertTriangle className="h-7 w-7 text-red-500 dark:text-red-400" />
                            </div>
                            <div className="mb-4 text-[clamp(28px,4vw,44px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">Unable to Load Tickets</div>
                            <div className={`${bodyMuted} mb-8`}>
                                {error instanceof Error && error.message.includes('404')
                                    ? 'No tickets found with the provided information. Please check your order number/hash ID and name, then try again.'
                                    : error instanceof Error
                                        ? error.message
                                        : 'We couldn\'t find your ticket information. Please check your order number/hash ID and name, then try again.'
                                }
                            </div>
                            <div className="mb-8 w-full max-w-md mx-auto rounded-2xl bg-yellow-50 p-4 ring-1 ring-yellow-200 dark:bg-yellow-900/20 dark:ring-yellow-800">
                                <div className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <div className="text-sm text-yellow-800 dark:text-yellow-300">
                                        <strong>Common issues:</strong>
                                        <ul className="mt-1 list-disc list-inside text-left">
                                            <li>Check that your order number/hash ID is correct</li>
                                            <li>Verify your name matches the one used during purchase</li>
                                            <li>Ensure there are no extra spaces in your input</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="flex w-full max-w-md flex-col gap-3 sm:flex-row">
                                <Link to="/order" className={`${btnPrimary} w-full sm:flex-1`}>
                                    Try Another Search
                                </Link>
                                <Link to="/" className={`${btnGhost} w-full sm:flex-1`}>
                                    Return to Home
                                </Link>
                            </div>
                        </div>
                    );
                })()}

                {orderResponse?.data && (
                    <div className="flex w-full max-w-4xl flex-col gap-10">
                        <div className="text-center sm:text-left">
                            <p className={eyebrow}>Order #{orderResponse.data.hashedUniqueId || 'N/A'}</p>
                            <h1 className={`${displayTitle} mt-4`}>Your Tickets</h1>
                            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 sm:justify-start">
                                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${orderResponse.data.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                    orderResponse.data.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                    }`}>
                                    {(orderResponse.data.status || 'unknown').charAt(0).toUpperCase() + (orderResponse.data.status || 'unknown').slice(1)}
                                </span>
                            </div>
                        </div>

                        {(isFromPayment || isDirectAccess) && (
                            <div className={`${card} w-full p-6 sm:p-8`}>
                                <div className="mb-5">
                                    <h2 className={`${cardTitle} mb-1`}>Order Number</h2>
                                    <p className="text-sm text-grey40 dark:text-nightMuted">
                                        Keep this order number safe! You'll need it to access your tickets later.
                                    </p>
                                </div>

                                <div className={`${surface} rounded-2xl p-4`}>
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                        <div className="min-w-0 flex-1">
                                            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">Order ID</span>
                                            <span className="block break-all font-mono text-lg font-bold tracking-wider text-grey10 dark:text-white">
                                                {orderResponse.data.hashedUniqueId || 'N/A'}
                                            </span>
                                        </div>
                                        <button
                                            onClick={copyOrderNumber}
                                            className={`${btnAccent} h-10 shrink-0 px-5`}
                                        >
                                            {copySuccess ? (
                                                <>
                                                    <Check className="h-4 w-4" />
                                                    Copied!
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="h-4 w-4" />
                                                    Copy
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-4 rounded-2xl bg-yellow-50 p-3 ring-1 ring-yellow-200 dark:bg-yellow-900/20 dark:ring-yellow-800">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
                                        <div className="text-xs text-yellow-800 dark:text-yellow-300">
                                            <strong>Important:</strong> Save this order number in a safe place. You'll need it to retrieve your tickets if you lose access to this page.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="w-full">
                            <div className="flex flex-col gap-6">
                                {(orderResponse.data.tickets || []).map((ticket, idx) => {
                                    // Safely access nested properties with fallbacks
                                    const event = (ticket?.event || {}) as any;
                                    const ticketId = (ticket?.ticketId || {}) as any;
                                    const times = (event?.times || []) as any[];
                                    const address = (event?.address || {}) as any;
                                    const banner = (event?.banner || {}) as any;

                                    // Use ticket.selectedDate (the date selected for this specific ticket) 
                                    // Format: "2025-08-15 18:30"
                                    const selectedDate = ticket?.selectedDate;
                                    let eventDate = 'TBD';

                                    if (selectedDate) {
                                        // Format date from selectedDate for display
                                        eventDate = formatDate(selectedDate);
                                    } else if (times.length > 0) {
                                        // Fallback to first event time if no selectedDate
                                        eventDate = formatDate(times[0]?.startDate);
                                    }

                                    // Format location with null checks
                                    const venue = event?.venue || '';
                                    const addressText = formatAddress(address);

                                    // Ensure we have valid ticket and event data
                                    const ticketTypeName = ticketId?.name || (ticket as any)?.ticketTitle || 'General Admission';
                                    const eventTitle = event?.title || (ticket as any)?.eventTitle || 'Event Title Not Available';
                                    const ticketKey = ticketId?._id || ticket?.ticketId?._id || (ticket?.ticketId as any) || `ticket-${idx}`;
                                    const basePrice = Number(ticket?.price || 0);
                                    const perUnitDiscount = Number((ticket as any)?.promocodeDiscount || 0) + Number((ticket as any)?.memberPricingDiscount || 0);
                                    const finalPrice = Math.max(0, basePrice - perUnitDiscount);
                                    const priceDisplay = perUnitDiscount > 0
                                        ? `${currencySymbol(orderResponse.data.currency)}${finalPrice.toFixed(2)} (-${currencySymbol(orderResponse.data.currency)}${perUnitDiscount.toFixed(2)})`
                                        : `${currencySymbol(orderResponse.data.currency)}${basePrice.toFixed(2)}`;

                                    return (
                                        <div key={ticketKey} className="flex justify-center">
                                            <ConcertTicket
                                                id={typeof id === 'string' ? id : ''}
                                                eventTitle={eventTitle}
                                                selectedDate={selectedDate || (times.length > 0 ? times[0]?.startDate : '')}
                                                venue={venue}
                                                address={addressText}
                                                ticketType={ticketTypeName}
                                                price={priceDisplay}
                                                orderNumber={orderResponse.data.orderId || 'N/A'}
                                                orderHashedId={orderResponse.data.hashedUniqueId || 'N/A'}
                                                ticketQr={ticket?.qrCode || ''}
                                                ticketNumber={ticket?.qrCode || ''}
                                                attendeeName={getAttendeeName(orderResponse.data, ticket)}
                                                eventImage={(banner as any)?.link || '/event1.png'}
                                                eventId={`${event?._id || (ticket as any)?.eventId || ''}`}
                                                event={event}
                                                seatData={ticket?.seatData as any || {}}
                                            />
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt-10 flex justify-center">
                                <div className={`${surface} w-full overflow-hidden`}>
                                    <div className="p-6 sm:p-8">
                                        <h3 className={`${cardTitle} mb-6`}>Order Summary</h3>
                                        <div className="space-y-4 text-sm">
                                            <div className="flex justify-between gap-4">
                                                <span className="text-grey40 dark:text-nightMuted">Order Date:</span>
                                                <span className="text-right font-semibold text-grey10 dark:text-white">{formatDate(orderResponse.data.createdAt)}</span>
                                            </div>
                                            <div className="flex justify-between gap-4">
                                                <span className="text-grey40 dark:text-nightMuted">Purchased by:</span>
                                                <span className="text-right font-semibold text-grey10 dark:text-white">{getAttendeeName(orderResponse.data)}</span>
                                            </div>
                                            <div className="flex justify-between gap-4">
                                                <span className="text-grey40 dark:text-nightMuted">Email:</span>
                                                <span className="min-w-0 break-all text-right font-semibold text-grey10 dark:text-white">{getEmail(orderResponse.data)}</span>
                                            </div>
                                            {getPhone(orderResponse.data) && (
                                                <div className="flex justify-between gap-4">
                                                    <span className="text-grey40 dark:text-nightMuted">Phone:</span>
                                                    <span className="text-right font-semibold text-grey10 dark:text-white">{getPhone(orderResponse.data)}</span>
                                                </div>
                                            )}
                                            {(() => {
                                                const ticketsTotal = (orderResponse.data.tickets || []).reduce((sum, t: any) => sum + Math.max(0, Number(t?.price || 0) - Number(t?.promocodeDiscount || 0) - Number(t?.memberPricingDiscount || 0)), 0);
                                                const serviceFee = Math.max(0, (orderResponse.data.totalAmount || 0) - ticketsTotal);
                                                if (serviceFee < 0.005) return null;
                                                return (
                                                    <>
                                                        <div className="flex justify-between gap-4">
                                                            <span className="text-grey40 dark:text-nightMuted">Tickets:</span>
                                                            <span className="text-right font-semibold text-grey10 dark:text-white">{currencySymbol(orderResponse.data.currency)}{ticketsTotal.toFixed(2)}</span>
                                                        </div>
                                                        <div className="flex justify-between gap-4">
                                                            <span className="text-grey40 dark:text-nightMuted">Service Fee:</span>
                                                            <span className="text-right font-semibold text-grey10 dark:text-white">{currencySymbol(orderResponse.data.currency)}{serviceFee.toFixed(2)}</span>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                            <div className="mt-2 border-t border-grey90 pt-5 dark:border-white/10">
                                                <div className="flex items-baseline justify-between gap-4 text-grey10 dark:text-white">
                                                    <span className="text-base font-semibold">Total Paid:</span>
                                                    <span className="text-[32px] font-bold leading-none tracking-[-0.03em] text-primary dark:text-nightAccent">{currencySymbol(orderResponse.data.currency)}{(orderResponse.data.totalAmount || 0).toFixed(2)}</span>
                                                </div>
                                            </div>
                                            {orderResponse.data.status === 'completed' && (
                                                <button
                                                    type="button"
                                                    onClick={handleDownloadReceipt}
                                                    disabled={isDownloadingReceipt}
                                                    className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-60 text-white font-semibold py-3 px-4 rounded mt-4"
                                                >
                                                    {isDownloadingReceipt ? 'Preparing receipt...' : 'View Receipt'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            </section>
            <Footer hasMobileNav />
            <MobileBottomNav />
        </div>
    );
} 